import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle } from "lucide-react";
import { whatsappLink } from "../../lib/api";
import StatusBadge from "./StatusBadge";

export default function ProductModal({ product, related = [], onClose, onSelectRelated }) {
  return (
    <AnimatePresence>
      {product && (
        <motion.div
          data-testid="product-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-charcoal/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="relative bg-cream w-full max-w-5xl max-h-[90vh] overflow-y-auto border border-charcoal/20"
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <button data-testid="modal-close" onClick={onClose} className="absolute top-4 right-4 z-10 p-2 bg-cream/90 text-charcoal hover:text-toga transition-colors">
              <X size={24} />
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative aspect-[4/5] overflow-hidden bg-charcoal/5">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4"><StatusBadge status={product.status} /></div>
              </div>
              <div className="p-8 md:p-12 flex flex-col">
                <span className="text-xs tracking-[0.28em] uppercase font-bold text-toga">{product.subcategory}</span>
                <h3 className="mt-3 font-display font-black uppercase tracking-tight text-charcoal text-3xl md:text-4xl leading-tight">{product.name}</h3>
                <p className="mt-5 font-light text-charcoal/70 leading-relaxed">{product.description}</p>
                {product.details && (
                  <div className="mt-6 pt-6 border-t border-charcoal/15">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-charcoal/50 mb-3">Details</p>
                    {product.details.includes("\n") ? (
                      <ul className="space-y-2">
                        {product.details.split("\n").filter(Boolean).map((line, i) => (
                          <li key={i} className="flex gap-3 font-light text-charcoal/80 leading-relaxed">
                            <span className="text-toga mt-2 shrink-0 h-1.5 w-1.5 bg-toga inline-block" />
                            <span>{line.replace(/^[-•\s]+/, "")}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="font-light text-charcoal/80 leading-relaxed">{product.details}</p>
                    )}
                  </div>
                )}
                <a
                  href={whatsappLink(`Hello TOGA, I'd like to inquire about "${product.name}" (${product.subcategory}). Is it available?`)}
                  target="_blank" rel="noreferrer"
                  data-testid="modal-inquire-btn"
                  className="mt-8 inline-flex items-center justify-center gap-3 bg-toga text-white px-8 py-4 text-xs uppercase tracking-[0.18em] font-bold hover:bg-charcoal transition-colors duration-300"
                >
                  <MessageCircle size={18} /> Inquire About This Design
                </a>

                {related.length > 0 && (
                  <div className="mt-10">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-charcoal/50 mb-4">Style Concierge — Pairs Well With</p>
                    <div className="flex gap-3">
                      {related.map((r) => (
                        <button key={r._id} onClick={() => onSelectRelated(r)} data-testid={`related-${r._id}`} className="group w-20 h-24 overflow-hidden border border-charcoal/15">
                          <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
